// Atmosphere — back-side fresnel glow around the planet. A
// SphereGeometry(1.7) with BackSide + AdditiveBlending so the rim
// glows where the camera sees the silhouette edges. Color and base
// opacity are driven by the active scene (scroll progress) —
// day = mint, dusk/contact = gold, night = mint dim, dawn = warm
// white — and slowly pulse around the base opacity on a 6s cycle so
// the planet feels alive without being noisy.

import { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import type { Mesh } from 'three';

import { sceneAtProgress } from './sceneState';
import { SCENES } from './planetSections';

const ATMOSPHERE_RADIUS = 1.78;
const PULSE_CYCLE_S = 6;
const PULSE_AMP = 0.045; // ± around scene base opacity

export function Atmosphere() {
  const meshRef = useRef<Mesh | null>(null);
  const matRef = useRef<THREE.MeshBasicMaterial | null>(null);
  const tmpColor = useRef(new THREE.Color()).current;
  const targetColor = useRef(new THREE.Color('#7fb89f')).current;
  const targetOpacity = useRef(0.18);

  useFrame((state) => {
    if (!matRef.current) return;
    const t = state.clock.elapsedTime;

    // Read live progress, find active scene, get scene atmosphere data.
    const p =
      typeof window !== 'undefined' && typeof window.__planetProgress === 'number'
        ? window.__planetProgress
        : 0;
    const { idx, blendIn } = sceneAtProgress(p);
    const scene =
      SCENES[idx] ?? SCENES[0];
    if (!scene) return;

    targetColor.set(scene.atmosphereColor);
    targetOpacity.current = scene.atmosphereOpacity;
    // Soft blend into the next chapter near the boundary.
    if (blendIn > 0.72 && idx < SCENES.length - 1) {
      const next = SCENES[idx + 1];
      const edge = (blendIn - 0.72) / 0.28;
      tmpColor.set(next.atmosphereColor);
      targetColor.lerp(tmpColor, edge);
      targetOpacity.current =
        scene.atmosphereOpacity +
        (next.atmosphereOpacity - scene.atmosphereOpacity) * edge;
    }

    // Lerp material color toward target (smooth scene transitions).
    matRef.current.color.lerp(targetColor, 0.05);

    // Sine pulse around the scene target.
    const k = (t / PULSE_CYCLE_S) * Math.PI * 2;
    const pulse = Math.sin(k) * PULSE_AMP;
    const desired = Math.max(0, targetOpacity.current + pulse);
    matRef.current.opacity = matRef.current.opacity + (desired - matRef.current.opacity) * 0.1;
  });

  return (
    <mesh ref={meshRef} name="atmosphere" raycast={() => null}>
      <sphereGeometry args={[ATMOSPHERE_RADIUS, 48, 32]} />
      <meshBasicMaterial
        ref={matRef}
        color="#7fb89f"
        transparent
        opacity={0.2}
        side={THREE.BackSide}
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </mesh>
  );
}

export default Atmosphere;
